import { useQuery } from '@tanstack/react-query';
import { Calendar, Clock, MapPin, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';

export const ScheduleWidget = () => {
  const { data: scheduleData, isLoading } = useQuery({
    queryKey: ['schedule-widget'],
    queryFn: async () => {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];

      const { data: schedules } = await supabase
        .from('team_schedules')
        .select(`
          id, 
          start_date, 
          end_date, 
          location, 
          assigned_guards,
          team_id,
          teams(name)
        `)
        .gte('start_date', today)
        .order('start_date', { ascending: true })
        .limit(20);

      const active = schedules?.filter(s => 
        fromLocalISOString(s.start_date) <= now && fromLocalISOString(s.end_date) >= now
      ).length || 0;

      const todaySchedules = schedules?.filter(s => 
        s.start_date.startsWith(today)
      ).length || 0;

      const tomorrowSchedules = schedules?.filter(s => 
        s.start_date.startsWith(tomorrow)
      ).length || 0;

      const totalGuardsScheduled = schedules?.reduce((sum, schedule) => 
        sum + (schedule.assigned_guards?.length || 0), 0
      ) || 0;

      return {
        total: schedules?.length || 0,
        active,
        today: todaySchedules,
        tomorrow: tomorrowSchedules,
        totalGuards: totalGuardsScheduled,
        upcoming: schedules?.slice(0, 5) || []
      };
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Calendar className="w-6 h-6 text-purple-500" />
        <h3 className="text-lg font-semibold">Schedule Overview</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{scheduleData?.active || 0}</div>
          <div className="text-sm text-muted-foreground">Active Now</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{scheduleData?.today || 0}</div>
          <div className="text-sm text-muted-foreground">Today's Shifts</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-orange-500" />
            <span className="text-sm">Tomorrow</span>
          </div>
          <Badge variant="outline">{scheduleData?.tomorrow || 0}</Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Guards Scheduled</span>
          </div>
          <Badge variant="default">{scheduleData?.totalGuards || 0}</Badge>
        </div>
      </div>

      {scheduleData?.upcoming && scheduleData.upcoming.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Upcoming Shifts</h4>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {scheduleData.upcoming.slice(0, 3).map((schedule) => (
              <div key={schedule.id} className="text-xs p-2 bg-muted rounded">
                <div className="flex justify-between items-center">
                  <span className="truncate">{schedule.location}</span>
                  <span className="text-muted-foreground">
                    {format(fromLocalISOString(schedule.start_date), 'MMM dd, HH:mm')}
                  </span>
                </div>
                <div className="text-muted-foreground">
                  {schedule.assigned_guards?.length || 0} guards • {schedule.teams?.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};